import type { CMSState as CoreCmsState, Page as CorePage } from '@ps/web-core-store';
import { cmsDefaultState, defaultLayout, models } from '@ps/web-core-store';
import type { ContentItem, GeoRedirect, Menu, Redirect, Website } from '@ps/web-martech-cms-types';
import type { SportsContentItem } from '@ps/web-sports-page-types';
import type { match } from 'react-router';
import type URL from 'url-parse';

import type { ModelConfig } from '@swf/core/store';

import effects from './effects';
import selectors from './selectors';

export interface PageResponse {
  page?: CorePage;
  content: Record<string, ContentItem | SportsContentItem>;
  redirect?: Redirect;
  geo_redirect?: GeoRedirect;
  alternate_links?: Website['alternate_links'];
}

export type PageResponseWithRouteMatch = PageResponse & {
  isRouteMatch: (path: string) => match<object> | null;
};

export interface MenuResponse {
  menu?: Menu;
  seo_menu?: Menu;
  search?: {
    qry: unknown[];
    includeTags: string[];
    newGameTileView: boolean;
    disableIcons: boolean;
    disableProviderLogos: boolean;
    overlayHierarchy: string;
  };
  content: Record<string, ContentItem | SportsContentItem>;
  depth?: Website['menu_depth'];
  seo_depth?: Website['seo_menu_depth'];
  menuView?: string;
}

export type CMSState = CoreCmsState &
  PageResponse &
  MenuResponse & { url?: URL; pageLoaded?: boolean; cacheControl?: string };

interface CMSModel extends ModelConfig<CMSState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export type CMSUpdateAction = Partial<CMSState>;

// TODO: CMS-migration - remove addDefaultPageLayout after stack content can be modified
const addDefaultPageLayout = (payload: CMSUpdateAction) => {
  if (payload.page && !payload.page.layout) {
    payload.page.layout = defaultLayout;
  }
  return payload;
};

const reducers = {
  ...models.cms.reducers,
  update: (state: CMSState, { content, url, ...payload }: CMSUpdateAction) => ({
    ...state,
    redirect: undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    geo_redirect: undefined,
    url,
    // TODO: CMS-migration - remove addDefaultPageLayout after stack content can be modified
    ...addDefaultPageLayout(payload),
    content: { ...state.content, ...content }
  }),
  updateContent: (state: CMSState, content: Record<string, ContentItem | SportsContentItem>) => ({
    ...state,
    content: { ...state.content, ...content }
  }),
  setPageLoaded: (state: CMSState, pageLoaded: boolean) => ({ ...state, pageLoaded })
};

const page: CMSModel = {
  state: cmsDefaultState,
  reducers,
  effects,
  selectors
};

export default page;
